/**
 * Acts as a clickable dropdown row, similar to DropdownTableRow but minus the <table>
 * @author Nikolas Bramblett (nbramblett@yext.com)
 */

import {ClickableDiv} from '/ui/components/clickable/clickablediv';

import * as styles from '/ui/components/dropdownrow/dropdownrow.module.scss';
import ArrowDown from '/ui/icons/inline/arrow-down.svg';
import ArrowRight from '/ui/icons/inline/arrow-right.svg';

/**
 * A component that renders an accordion row which when expanded, shows the expanded content.
 * This component is responsible for maintaining the state of expansion, but provides an update
 * to the parent when the component is expanded/collapsed.
 *
 * This component requires two children components
 *  1. {@link DropdownSummary} which is responsible for rendering the column names of the row
 *  2. {@link DropdownContent} which is responsible for rendering the data that is to be shown
 *  when the row is expanded.
 *
 * An example on how to use this component is provided below.
 * <DropdownRow>
 *   <DropdownSummary>
 *     {'column-1'}
 *     {'column-2}
 *   </DropdownSummary>
 *   <DropdownContent>
 *     {'This will be shown when the row is expanded'}
 *   </DropdownContent>
 * </DropdownRow>
 *
 * @param {{
 *  children?: ?,
 *  className?: string,
 *  tid?: string,
 *  onToggleExpand: (function():void),
 *  isExpanded: boolean,
 * }} props
 * @return {React.ReactElement}
 */
export const DropdownRow = props => {
  const {className, tid, onToggleExpand, isExpanded} = props;

  /**
   * Children can either be an array or a single object. This allows us to deal with them as if they
   * are always an array.
   * Also filters out all 'null' children.
   */
  const children = [].concat(props.children).filter(child => child);

  /**
   * On click handler for when the expand icon is clicked.
   * Expands the row and calls the user sent in onExpand if available.
   */
  const toggleExpandRow = () => {
    onToggleExpand();
  };

  const dropdownSummary = children.find(
    child => child.type && child.type.name === DropdownSummary.name,
  );

  if (!dropdownSummary) {
    throw new Error('DropdownRow must have a summary');
  }

  const dropdownContent = children.find(
    child => child.type && child.type.name === DropdownContent.name,
  );

  if (!dropdownContent) {
    throw new Error('DropdownRow must have content');
  }


  return (
    <React.Fragment>
      <ClickableDiv
        className={className || styles.dropdownrow}
        onClick={toggleExpandRow}
        tid={tid}
      >
        <div className={styles.dropdownrowIcon}>
          {isExpanded ? <ArrowDown /> : <ArrowRight />}
        </div>
        {dropdownSummary}
      </ClickableDiv>
      {isExpanded && (
        <div className={dropdownContent.props.className || styles.dropdownrowContent}>
          {dropdownContent}
        </div>
      )}
    </React.Fragment>
  );
};

/**
 * A component that's used to render a summary of content for {@link DropdownRow} while collapsed
 * @param {{
 *   children?: React.ReactElement,
 * }} props
 * @return {React.ReactElement | null}
 */
export const DropdownSummary = props => {
  return props.children || null;
};

/**
 * A component that's used to render the dropdown content for {@link DropdownRow} while expanded
 * @param {{
 *   className?: string,
 *   children?: React.ReactElement,
 * }} props
 * @return {React.ReactElement | null}
 */
export const DropdownContent = props => {
  return props.children || null;
};
